import { Database, BookOpen, Users, Megaphone, FileText, Sparkles, BarChart3 } from "lucide-react";
import { BRAND_KNOWLEDGE_NODES } from "@/data/growthOSData";

const ICONS = [BookOpen, Sparkles, Users, Megaphone, FileText, Database, BarChart3];

/**
 * Slide 4 — Brand Intelligence Layer.
 * Generic "Brand Agent" title with knowledge core + orbiting nodes.
 * Fits within parent's height; nothing clips.
 */
export default function BrandIntelligenceCore({ brand, compact = false }) {
  if (!brand) return null;

  if (compact) {
    return (
      <div className="relative w-full h-full">
        <div className="absolute inset-[10%] rounded-full" style={{ background: `radial-gradient(circle, ${brand.accent}40 0%, transparent 70%)`, filter: "blur(16px)" }} />
        {[0,1,2].map((r)=>(
          <div key={r} className="absolute rounded-full border" style={{ inset: `${5+r*8}%`, borderColor: `${brand.accent}${r===0?"55":r===1?"30":"18"}`, animation: `spin-slow ${18+r*8}s linear ${r%2===0?"":"reverse"} infinite` }} />
        ))}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full flex items-center justify-center"
          style={{ background: `radial-gradient(circle at 30% 25%, ${brand.accent}99, ${brand.accent}22 60%, transparent 80%)`, boxShadow: `0 0 30px ${brand.accent}80` }}>
          <span className="font-heading text-[10px] text-white tracking-wide">Brand Brain</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative w-full h-full flex flex-col items-center justify-center px-4"
      data-testid="brand-intelligence-core"
    >
      {/* Title */}
      <div className="text-center mb-3 sm:mb-4 shrink-0">
        <div
          className="text-[9px] sm:text-[10px] tracking-[0.4em] font-mono uppercase mb-1"
          style={{ color: brand.accent, opacity: 0.85 }}
        >
          Layer 03.5 — Brand Memory
        </div>
        <h3
          className="font-heading text-2xl sm:text-3xl font-light tracking-tight text-white"
          style={{ textShadow: `0 0 24px ${brand.accent}55` }}
        >
          Brand Agent
        </h3>
        <div className="text-[10px] sm:text-xs font-mono tracking-[0.32em] uppercase text-white/45 mt-0.5">
          Internal Memory
        </div>
      </div>

      {/* Knowledge core ring — responsive size, fits within available height */}
      <div
        className="relative aspect-square shrink-0"
        style={{ width: "min(58vh, 540px)" }}
      >
        {/* Outer glow */}
        <div
          className="absolute inset-[15%] rounded-full"
          style={{
            background: `radial-gradient(circle, ${brand.accent}30 0%, transparent 70%)`,
            filter: "blur(20px)",
          }}
        />

        {/* Concentric rings */}
        {[0, 1, 2].map((r) => (
          <div
            key={r}
            className="absolute rounded-full border"
            style={{
              inset: `${10 + r * 8}%`,
              borderColor: `${brand.accent}${r === 0 ? "55" : r === 1 ? "30" : "18"}`,
              animation: `spin-slow ${20 + r * 8}s linear ${r % 2 === 0 ? "" : "reverse"} infinite`,
            }}
          />
        ))}

        {/* Center brain glyph */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <div
            className="w-24 h-24 sm:w-32 sm:h-32 rounded-full flex items-center justify-center relative"
            style={{
              background: `radial-gradient(circle at 30% 25%, ${brand.accent}99, ${brand.accent}22 60%, transparent 80%)`,
              boxShadow: `0 0 40px ${brand.accent}80, inset 0 0 20px ${brand.accent}40`,
            }}
          >
            <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
              {Array.from({ length: 16 }).map((_, i) => {
                const a = (i / 16) * Math.PI * 2;
                const r1 = 26 + (i % 3) * 4;
                const r2 = 14 + ((i + 1) % 4) * 3;
                const x1 = 50 + Math.cos(a) * r1;
                const y1 = 50 + Math.sin(a) * r1;
                const x2 = 50 + Math.cos(a + 1.2) * r2;
                const y2 = 50 + Math.sin(a + 1.2) * r2;
                return (
                  <g key={i}>
                    <line
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                      stroke={brand.accent}
                      strokeOpacity="0.55"
                      strokeWidth="0.4"
                    />
                    <circle cx={x1} cy={y1} r="0.9" fill="#fff" opacity="0.85">
                      <animate
                        attributeName="opacity"
                        values="0.3;1;0.3"
                        dur={`${1.4 + (i % 5) * 0.3}s`}
                        repeatCount="indefinite"
                        begin={`${i * 0.07}s`}
                      />
                    </circle>
                  </g>
                );
              })}
            </svg>
            <span className="font-heading text-sm sm:text-base font-light text-white tracking-wide z-10">
              Brand Brain
            </span>
          </div>
        </div>

        {/* Orbiting knowledge nodes */}
        {BRAND_KNOWLEDGE_NODES.map((label, i) => {
          const angle =
            (i / BRAND_KNOWLEDGE_NODES.length) * Math.PI * 2 - Math.PI / 2;
          const r = 42;
          const x = 50 + Math.cos(angle) * r;
          const y = 50 + Math.sin(angle) * r;
          const Icon = ICONS[i % ICONS.length];
          return (
            <div
              key={label}
              className="absolute -translate-x-1/2 -translate-y-1/2"
              style={{
                left: `${x}%`,
                top: `${y}%`,
                animation: `knowledge-in 0.6s ${0.15 + i * 0.08}s both`,
              }}
              data-testid={`knowledge-node-${i}`}
            >
              <div
                className="glass-strong rounded-lg px-2 py-1.5 flex items-center gap-1.5 whitespace-nowrap"
                style={{
                  borderTop: `1px solid ${brand.accent}88`,
                  boxShadow: `0 0 18px ${brand.accent}40`,
                }}
              >
                <Icon
                  className="w-3 h-3"
                  style={{ color: brand.accent }}
                  strokeWidth={1.6}
                />
                <span className="text-[9px] sm:text-[10px] tracking-[0.14em] uppercase font-mono text-white/85">
                  {label}
                </span>
              </div>
            </div>
          );
        })}

        {/* Connection lines */}
        <svg
          className="absolute inset-0 pointer-events-none w-full h-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          {BRAND_KNOWLEDGE_NODES.map((_, i) => {
            const angle =
              (i / BRAND_KNOWLEDGE_NODES.length) * Math.PI * 2 - Math.PI / 2;
            const r = 38;
            const x = 50 + Math.cos(angle) * r;
            const y = 50 + Math.sin(angle) * r;
            const innerR = 14;
            const ix = 50 + Math.cos(angle) * innerR;
            const iy = 50 + Math.sin(angle) * innerR;
            const d = `M ${ix} ${iy} L ${x} ${y}`;
            return (
              <g key={i}>
                <path
                  d={d}
                  stroke={brand.accent}
                  strokeOpacity="0.32"
                  strokeWidth="0.25"
                  strokeDasharray="0.6 1.2"
                />
                <circle r="0.6" fill={brand.accent}>
                  <animateMotion
                    dur={`${2 + i * 0.2}s`}
                    repeatCount="indefinite"
                    begin={`${i * 0.18}s`}
                    path={d}
                  />
                </circle>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Caption */}
      <p className="mt-4 text-center text-white/55 text-[11px] sm:text-xs font-body tracking-wide max-w-md px-4 shrink-0">
        Each Brand Agent develops its own competitive advantage — and continuously contributes what it learns back to the Brain.
      </p>
    </div>
  );
}
